/**
 * useKeyboardShortcuts — Sequence-aware keyboard shortcut system.
 *
 * Supports both modifier shortcuts (Cmd+K) and two-key sequences (N→I, G→D).
 * Sequences time out after 500ms. Ignores events when focus is in input fields.
 *
 * @since 1.0.0
 */
import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { togglePanels } from '@/shared/panelLock/panelKeys';

export interface ShortcutDef {
    keys: string;
    label: string;
    category: 'navigation' | 'action' | 'general';
}

// Static shortcut metadata (key combos + labels) — never changes, no refs involved.
const SHORTCUT_META: { key1: string; key2?: string; modifier?: boolean; label: string; category: ShortcutDef['category']; id: string }[] = [
    // Modifier shortcuts
    { id: 'cmd-k', key1: 'k', modifier: true, label: 'Command Palette', category: 'general' },
    { id: 'cmd-n', key1: 'n', modifier: true, label: 'New Transaction', category: 'action' },
    // N→X sequences
    { id: 'n-i', key1: 'n', key2: 'i', label: 'New Invoice', category: 'action' },
    { id: 'n-b', key1: 'n', key2: 'b', label: 'New Bill', category: 'action' },
    { id: 'n-e', key1: 'n', key2: 'e', label: 'Quick Expense', category: 'action' },
    // G→X sequences
    { id: 'g-d', key1: 'g', key2: 'd', label: 'Go to Dashboard', category: 'navigation' },
    { id: 'g-s', key1: 'g', key2: 's', label: 'Go to Sales', category: 'navigation' },
    { id: 'g-p', key1: 'g', key2: 'p', label: 'Go to Purchases', category: 'navigation' },
    { id: 'g-r', key1: 'g', key2: 'r', label: 'Go to Reports', category: 'navigation' },
    { id: 'g-c', key1: 'g', key2: 'c', label: 'Go to Contacts', category: 'navigation' },
    { id: 'g-j', key1: 'g', key2: 'j', label: 'Go to Journals', category: 'navigation' },
    { id: 'g-a', key1: 'g', key2: 'a', label: 'Go to Accounts', category: 'navigation' },
    { id: 'g-i', key1: 'g', key2: 'i', label: 'Go to Items', category: 'navigation' },
    { id: 'g-t', key1: 'g', key2: 't', label: 'Go to Tasks', category: 'navigation' },
    // Single-key
    { id: 'help', key1: '?', label: 'Keyboard Shortcuts', category: 'general' },
    // panellock (19 Oct 2026): either side panel open, shut both; both shut, open both.
    { id: 's-s', key1: ']', label: 'Open or shut the sidebar and Act Now', category: 'general' },
];

// Pre-computed help overlay definitions (derived from static metadata).
const SHORTCUT_DEFS: ShortcutDef[] = SHORTCUT_META.map(s => ({
    keys: s.modifier ? `⌘${s.key1.toUpperCase()}` : s.key2 ? `${s.key1.toUpperCase()} → ${s.key2.toUpperCase()}` : s.key1,
    label: s.label,
    category: s.category,
}));

// Navigation route map for G→X shortcuts.
const NAV_ROUTES: Record<string, string> = {
    'd': '/',
    's': '/sales',
    'p': '/purchases',
    'r': '/reports',
    'c': '/contacts',
    'j': '/journals',
    'a': '/accounts',
    'i': '/items',
    't': '/tasks',
};

interface KeyboardCallbacks {
    onCommandPalette: () => void;
    onNewTransaction: () => void;
    onNewInvoice: () => void;
    onNewBill: () => void;
    onNewExpense: () => void;
    onShortcutHelp: () => void;
}

/**
 * Register global keyboard shortcuts including two-key sequences.
 *
 * @param callbacks - Object with callbacks for specific actions
 * @returns Array of shortcut definitions (for help overlay)
 */
export function useKeyboardShortcuts(callbacks: KeyboardCallbacks): ShortcutDef[] {
    const navigate = useNavigate();
    const pendingKeyRef = useRef<string | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Stable action dispatcher — maps shortcut IDs to the correct callback.
    // Uses `navigate` directly (stable from TanStack) and callbacks via
    // a lookup that always reads the latest cb from the param (the handler
    // is re-registered when callbacks change thanks to the dep array).
    const dispatch = useCallback((id: string, key2?: string) => {
        switch (id) {
            case 'cmd-k': callbacks.onCommandPalette(); break;
            case 'cmd-n': callbacks.onNewTransaction(); break;
            case 'n-i': callbacks.onNewInvoice(); break;
            case 'n-b': callbacks.onNewBill(); break;
            case 'n-e': callbacks.onNewExpense(); break;
            case 'help': callbacks.onShortcutHelp(); break;
            case 's-s': togglePanels(); break;
            default:
                // G→X navigation
                if (id.startsWith('g-') && key2 && NAV_ROUTES[key2]) {
                    navigate({ to: NAV_ROUTES[key2] });
                }
                break;
        }
    }, [callbacks, navigate]);

    const clearPending = useCallback(() => {
        pendingKeyRef.current = null;
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    }, []);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            // Skip when typing in inputs
            const target = e.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable) return;

            const key = e.key.toLowerCase();
            const hasModifier = e.metaKey || e.ctrlKey;

            // 1. Modifier shortcuts are handled by AppLayout — skip to avoid double-triggering
            if (hasModifier) return;

            // 2. If we have a pending first key, check for second key match
            if (pendingKeyRef.current) {
                const pending = pendingKeyRef.current;
                clearPending();
                const match = SHORTCUT_META.find(s => !s.modifier && s.key1 === pending && s.key2 === key);
                if (match) {
                    e.preventDefault();
                    dispatch(match.id, key);
                    return;
                }
            }

            // 3. Check single-key shortcuts (no key2)
            const singleMatch = SHORTCUT_META.find(s => !s.modifier && !s.key2 && s.key1 === key);
            if (singleMatch) {
                e.preventDefault();
                dispatch(singleMatch.id);
                return;
            }

            // 4. Check if this could be the first key of a sequence
            const couldBeFirst = SHORTCUT_META.some(s => !s.modifier && s.key2 && s.key1 === key);
            if (couldBeFirst) {
                pendingKeyRef.current = key;
                timerRef.current = setTimeout(clearPending, 500);
            }
        };

        document.addEventListener('keydown', handler);
        return () => {
            document.removeEventListener('keydown', handler);
            clearPending();
        };
    }, [clearPending, dispatch]);

    // Return pre-computed static definitions — no refs, no render-time computation.
    return useMemo(() => SHORTCUT_DEFS, []);
}
